'use client';

import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';

import { JsonLdFaqPage } from '@/components/SEO';
import { useGetFaqsByType } from '@/lib/react-query/FaqQueries';
import { cn } from '@/lib/utils';

type Props = {
  className?: string;
  type: 'piercing' | 'tattoo';
  theme?: 'amber' | 'emerald';
};

const FAQ = ({ className = '', type, theme }: Props) => {
  const { data: faqs, error, isLoading } = useGetFaqsByType(type);

  if (isLoading || !faqs || error) {
    return null;
  }

  const bgColor = theme === 'emerald' ? 'bg-emerald-50' : 'bg-amber-50';

  return (
    <div className={cn(bgColor, className)}>
      <JsonLdFaqPage faqs={faqs} />
      <div className="mx-auto max-w-7xl px-6 py-4 lg:px-8">
        <div className="mx-auto max-w-4xl divide-y divide-gray-900/10">
          <h2 className="text-2xl leading-10 font-bold tracking-tight text-gray-900">
            Questions fréquentes
          </h2>
          <dl className="mt-10 space-y-6 divide-y divide-gray-900/10">
            {faqs.map((faq) => (
              <Disclosure as="div" key={faq.id} className="pt-6">
                {({ open }) => (
                  <>
                    <dt>
                      <DisclosureButton className="flex w-full cursor-pointer items-start justify-between text-left text-gray-900">
                        <span className="text-base leading-7 font-semibold">{faq.label}</span>
                        <span className="ml-6 flex h-7 items-center">
                          {open ? (
                            <MinusIcon className="h-6 w-6" aria-hidden="true" />
                          ) : (
                            <PlusIcon className="h-6 w-6" aria-hidden="true" />
                          )}
                        </span>
                      </DisclosureButton>
                    </dt>
                    <DisclosurePanel as="dd" className="mt-2 pr-12">
                      <p className="text-base leading-7 text-gray-600">{faq.content}</p>
                    </DisclosurePanel>
                  </>
                )}
              </Disclosure>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
